import { supabase } from '../../supabase';
import { ESTIMATE_RESPONSES_SETTINGS_KEY, ESTIMATE_RESPONSE_NOTIFY_SETTINGS_KEY } from './estimateResponseSettings';
import { OFFICE_TASK_NOTIFY_SETTINGS_KEY } from './officeTaskSettings';
import { normalizeCompanySettings, sanitizeCompanySettings, changedEditableKeys } from './companySettingsShape';
import { emitNotifyListAuditEvents } from './companySettingsAudit';
import {
  getCompanySettingsRpcCapability,
  setCompanySettingsRpcCapability,
  isMissingRpcError,
} from './companySettingsRpcCapability';

/**
 * Client half of `public.update_company_settings(p_patch jsonb)` (migration
 * 20260929120000): owner + MFA, tenant from the JWT, allowlisted + validated
 * keys, atomic `settings || p_patch` merge under a row lock, server-side audit
 * (`settings:edited`, `settings:estimate_responses_policy_changed`).
 *
 * The RPC validates JSON TYPES strictly (booleans must be JSON booleans, numbers
 * numbers-or-null, strings strings), so `toRpcPatchValue` coerces every value
 * the form can hold into the exact type the SQL allowlist expects. Key names are
 * the SAME list as `editableCompanySettingsKeys` — the SQL `v_allowed` mirrors it.
 */

const BOOLEAN_KEYS = new Set([
  'taxEnabled', 'voidNotifyDefaultSent', 'voidNotifyDefaultPaid',
  'autoEstimateRemindersEnabled', 'autoInvoiceRemindersEnabled',
  ESTIMATE_RESPONSES_SETTINGS_KEY,
]);
const NUMBER_KEYS = new Set([
  'taxRate', 'invoiceNetTermsDays', 'photoRetentionDays',
  'credit_billing_grant_cap', 'credit_alert_threshold', // KAN-784: dollars, 2 decimals
]);
// Integer-only on the server (invoiceNetTermsDays 0–365, photoRetentionDays 1–3650);
// taxRate is a fractional 0–1 rate.
const INTEGER_KEYS = new Set(['invoiceNetTermsDays', 'photoRetentionDays']);
const CENTS_KEYS = new Set(['credit_billing_grant_cap', 'credit_alert_threshold']);
const ARRAY_KEYS = new Set([ESTIMATE_RESPONSE_NOTIFY_SETTINGS_KEY, OFFICE_TASK_NOTIFY_SETTINGS_KEY]);

/** The value to send for one settings key, typed as the RPC's validator wants. */
export const toRpcPatchValue = (key, value) => {
  if (BOOLEAN_KEYS.has(key)) return value === true;
  if (NUMBER_KEYS.has(key)) {
    let n = null;
    if (typeof value === 'number') n = value;
    else if (typeof value === 'string' && value.trim() !== '') n = Number(value);
    if (n === null || !Number.isFinite(n)) return null; // blank / non-numeric = cleared
    if (CENTS_KEYS.has(key)) return Math.round(n * 100) / 100;
    return INTEGER_KEYS.has(key) ? Math.round(n) : n;
  }
  if (ARRAY_KEYS.has(key)) {
    return Array.isArray(value) ? value.filter((v) => typeof v === 'string') : [];
  }
  if (value === null) return null;
  if (value === undefined) return '';
  return typeof value === 'string' ? value : String(value);
};

/** `{ key: typedValue }` for exactly `keys`, read from the sanitized settings. */
export const buildRpcPatch = (settings, keys) => {
  const patch = {};
  keys.forEach((key) => { patch[key] = toRpcPatchValue(key, settings[key]); });
  return patch;
};

const RPC_TOKENS = [
  'MFA_REQUIRED', 'NOT_AUTHORIZED',
  'INVALID_SETTINGS_VALUE', 'INVALID_SETTINGS_KEY', 'INVALID_SETTINGS_PATCH',
];

/**
 * An Error carrying the server token as `.code` (so `getEdgeMfaGateErrorMessage`
 * and `getCompanySettingsRpcErrorMessage` can recognise it) and the offending
 * settings key as `.settingsField` (the RPC puts it in DETAIL).
 */
export const toCompanySettingsRpcError = (error) => {
  const message = typeof error?.message === 'string' ? error.message : 'Unknown error';
  const token = RPC_TOKENS.find((t) => message.includes(t));
  const err = new Error(message);
  err.code = token || error?.code;
  if (token && typeof error?.details === 'string' && error.details) err.settingsField = error.details;
  return err;
};

/** Never throws on a missing function; a missing rpc is reported as `missing`. */
async function callRpc(patch) {
  const { data, error, status } = await supabase.rpc('update_company_settings', { p_patch: patch });
  if (isMissingRpcError(error, status)) {
    setCompanySettingsRpcCapability('legacy');
    return { missing: true };
  }
  // Only a real SERVER answer proves the function exists (and with it the audit
  // triggers shipped in the same migration): success, or one of its own raised
  // tokens / SQLSTATEs. postgrest-js does NOT throw on a network failure — it
  // returns `{ error, status: 0 }` — and a 5xx / PGRST002 proves nothing, so
  // those leave the capability untouched (a blip must never latch 'rpc' on a
  // database without the migration, which would drop the 2FA audit row).
  if (!error || isServerAnswer(error)) setCompanySettingsRpcCapability('rpc');
  return error ? { error: toCompanySettingsRpcError(error) } : { data };
}

const SERVER_SQLSTATES = new Set(['42501', '22023']);
function isServerAnswer(error) {
  const message = typeof error?.message === 'string' ? error.message : '';
  return SERVER_SQLSTATES.has(error?.code)
    || RPC_TOKENS.some((t) => message.includes(t))
    || message.includes('USE_RPC');
}

/**
 * Cheap capability probe for the 2FA-policy audit decision: an EMPTY patch is a
 * no-write, no-audit call. Missing function → 'legacy'; success or a real
 * server answer (e.g. NOT_AUTHORIZED) → 'rpc'. A transport failure (returned
 * status 0 / 5xx, or thrown) leaves the flag 'unknown' and answers 'legacy' for
 * this call (keep the client emit: never zero).
 */
export async function ensureCompanySettingsRpcCapability() {
  const known = getCompanySettingsRpcCapability();
  if (known !== 'unknown') return known;
  try {
    await callRpc({});
  } catch (_) {
    return 'legacy';
  }
  return getCompanySettingsRpcCapability();
}

/**
 * Save ONLY the keys changed since `baseline` through the RPC.
 * Resolves `null` when the function is not deployed here (caller falls back to
 * the direct-update path, unchanged); otherwise the `{ data, error }` result.
 * An empty patch skips the network entirely.
 */
export async function saveCompanySettingsViaRpc({ settings, baseline, tenantId }) {
  const keys = changedEditableKeys(settings, baseline);
  if (keys.length === 0) return { data: normalizeCompanySettings(baseline), error: null };

  const patch = buildRpcPatch(settings, keys);
  const outcome = await callRpc(patch);
  if (outcome.missing) return null;
  if (outcome.error) return { data: normalizeCompanySettings(settings), error: outcome.error };

  const returned = outcome.data && typeof outcome.data === 'object' && !Array.isArray(outcome.data)
    ? outcome.data
    : { ...baseline, ...patch };
  // The server wrote `settings:edited` + the estimate-responses toggle row. The
  // two notify-list rows stay here: they denormalize user names the DB does not look up.
  // Diff ONLY what this save sent: baseline vs baseline+patch. Diffing against
  // the server's merged result would blame this user for a notify-list change
  // another device made after this form loaded.
  emitNotifyListAuditEvents({
    tenantId,
    priorSettings: sanitizeCompanySettings(baseline),
    settings: sanitizeCompanySettings({ ...baseline, ...patch }),
  });
  return { data: normalizeCompanySettings(returned), error: null };
}
